import React, { useState } from 'react';

import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { Image } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import type { UpdateFMXGroupBody } from '../../../@types/fmxGroup';
import { selectedVisitAtom } from '../../../app/appHeader/imageViewerMenu/atoms';
import { addMessage } from '../../../app/snackbar/SnackbarActions';
import { useUpdateFMXGroup } from '../../../react-query/useUpdateFMXGroup';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import { swapImageIndexes } from '../AnnotatorActions';
import type { ImageObject } from '../types';

export const DragAndDropContext = ({
  children,
  imagesArray,
}: {
  children: React.ReactNode;
  imagesArray: ImageObject[];
}) => {
  const [activeImageBeingDragged, setActiveImageBeingDragged] = useState<{
    id: string | number;
    container: string;
  }>();
  const { mutateAsync: updateFMXGroup } = useUpdateFMXGroup();
  const estimatedVisitId = useAtomValue(selectedVisitAtom);

  const { id: selectedPatientId, imageId: selectedImageId } = useParams<{
    id: string;
    imageId?: string;
  }>();
  // This sensor allows us to click on the draggable images  w/o immediately triggering the drag and drop flow
  // so that we can take the link to the image.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const dispatch = useDispatch();

  const { trackEvent } = useTrackEvent();

  const customCollisionDetectionAlgorithm = ({
    droppableContainers,
    ...args
  }: any) => {
    if (selectedImageId) {
      return [];
    }
    // First, let's see if the droppable box in image viewer is intersecting
    const rectIntersectionCollisions = rectIntersection({
      ...args,
      droppableContainers: droppableContainers.filter(
        ({ id }: any) =>
          id === 'droppable' ||
          id === 'image-grid-droppable-left' ||
          id === 'image-grid-droppable-right',
      ),
    });

    // Collision detection algorithms return an array of collisions
    if (rectIntersectionCollisions.length > 0) {
      // The droppable box in image viewer is intersecting, return early
      return rectIntersectionCollisions;
    }

    // Compute other collisions
    return closestCenter({
      ...args,
      droppableContainers: droppableContainers.filter(
        ({ id }: any) =>
          id !== 'droppable' &&
          id !== 'image-grid-droppable-left' &&
          id !== 'image-grid-droppable-right',
      ),
    });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active: imageBeingDragged, over: droppableContainer } = event;
    if (
      !imageBeingDragged ||
      !droppableContainer ||
      imageBeingDragged.id === droppableContainer.id
    ) {
      return;
    }

    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const imageBeingDraggedSortable = imageBeingDragged.data.current.sortable;
    const droppableContainerSortable =
      droppableContainer.data.current?.sortable;
    const isImageBeingDraggedContainerFMX =
      imageBeingDraggedSortable?.containerId === 'fmx-viewer';
    const isDroppableContainerContainerFMX =
      droppableContainerSortable?.containerId === 'fmx-viewer';

    const findImageIdFromFMXIndexOrId = (
      isFmxContainer: boolean,
      sortableId: string,
    ) => {
      // If the draggable image is from the FMX view, the id will contain the fmxIndex which we can then use to find the imageId
      // If the draggable image is from the Image Viewer, the id will contain the image's id
      if (isFmxContainer) {
        const image = imagesArray.find(
          (i) => i.fmxIndex !== -1 && i.fmxIndex === parseInt(sortableId),
        );

        return { imageId: image?.imageId, analysisId: image?.analysisId };
      }
      return {
        imageId: sortableId,
        analysisId: imagesArray.find((i) => i.imageId === sortableId)
          ?.analysisId,
      };
    };

    const imageBeingDraggedImageId = findImageIdFromFMXIndexOrId(
      isImageBeingDraggedContainerFMX,
      imageBeingDragged.id as string,
    );
    // If the droppable container we are over is not a sortable item, then it is the droppable container inside of image viewer which has no imageId
    const droppableContainerImageId = droppableContainerSortable
      ? findImageIdFromFMXIndexOrId(
          isDroppableContainerContainerFMX,
          droppableContainer.id as string,
        )
      : undefined;
    const imageBeingDraggedImageGroup = isImageBeingDraggedContainerFMX
      ? 'FMX'
      : 'OTHER';
    const droppableContainerImageGroup = isDroppableContainerContainerFMX
      ? 'FMX'
      : 'OTHER';
    const imageBeingDraggedFmx = isImageBeingDraggedContainerFMX
      ? parseInt(imageBeingDragged.id as string)
      : -1;
    const droppableContainerFmx = isDroppableContainerContainerFMX
      ? parseInt(droppableContainer.id as string)
      : -1;

    const updateFmxGroupArray: UpdateFMXGroupBody[] = [
      {
        imageId: imageBeingDraggedImageId.imageId as string,
        analysisId: imageBeingDraggedImageId.analysisId,
        imageGroup: droppableContainerImageGroup,
        newSlot: isDroppableContainerContainerFMX
          ? (droppableContainerFmx as number)
          : undefined,
      },
    ];
    // Some of the droppable containers don't have image ids and thus shouldn't be updated
    if (droppableContainerImageId?.imageId) {
      updateFmxGroupArray.push({
        imageId: droppableContainerImageId.imageId,
        analysisId: droppableContainerImageId.analysisId,
        imageGroup: imageBeingDraggedImageGroup,
        newSlot: isImageBeingDraggedContainerFMX
          ? (imageBeingDraggedFmx as number)
          : undefined,
      });
    }
    dispatch(
      swapImageIndexes(
        selectedPatientId,
        {
          imageId: imageBeingDraggedImageId.imageId,
          imageGroup: imageBeingDraggedImageGroup,
          fmxIndex: imageBeingDraggedFmx,
        },
        {
          imageId: droppableContainerImageId?.imageId,
          imageGroup: droppableContainerImageGroup,
          fmxIndex: droppableContainerFmx,
        },
      ),
    );
    try {
      if (estimatedVisitId) {
        await updateFMXGroup({
          updateFmxGroupBody: updateFmxGroupArray,
          visitId: parseInt(estimatedVisitId),
        });
        trackEvent(mixpanelEvents.dragAndDrop, {
          imageBeingDragged: {
            imageId: imageBeingDraggedImageId.imageId,
            imageGroup: imageBeingDraggedImageGroup,
            fmxIndex: imageBeingDraggedFmx,
          },
          destination: {
            imageId: droppableContainerImageId?.imageId,
            imageGroup: droppableContainerImageGroup,
            fmxIndex: droppableContainerFmx,
          },
        });
      }
    } catch (error) {
      dispatch(addMessage('Failed to save FMX changes'));
    }
  };
  return (
    <DndContext
      collisionDetection={customCollisionDetectionAlgorithm}
      onDragEnd={handleDragEnd}
      onDragStart={(dragEvent) => {
        setActiveImageBeingDragged({
          id: dragEvent.active.id,
          // @ts-expect-error TS(2532): Object is possibly 'undefined'.
          container: dragEvent.active.data.current.sortable.containerId,
        });
      }}
      sensors={sensors}
    >
      {children}
      {/* Higher z-index than app bar */}
      <DragOverlay zIndex={1300} modifiers={[snapCenterToCursor]}>
        {!!activeImageBeingDragged && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="75px"
            height="60px"
            // fixes odd positioning issue due to sizing of image containers in FMX
            marginTop={
              activeImageBeingDragged.container === 'image-bucket' ||
              activeImageBeingDragged.container === 'image-grid'
                ? ''
                : '50px'
            }
            bgcolor="black"
            color="white"
          >
            <Image />
          </Box>
        )}
      </DragOverlay>
    </DndContext>
  );
};
